/**
 * Per-model alignment of the generated-MEP overlay to the Fragments mesh.
 *
 * The MEP engine works in the plan coordinates of the extracted space
 * footprints (raw web-ifc world coordinates), while the Fragments importer
 * places the building mesh in its own (recentred) coordinates. The two agree
 * on axes (plan x → three.js x, plan y → three.js z, elevation → three.js y;
 * see `planToThree`) but differ by a constant per-model translation.
 *
 * This module recovers that translation from per-space evidence: footprint
 * centroids + storey elevations on the plan side vs Fragments bounding boxes
 * of the same `IfcSpace` items (matched by IFC express id / Fragments local
 * id) on the mesh side. Medians keep the estimate robust against odd spaces
 * (slanted roof volumes, hull-vs-box centroid skew).
 */

/** One space's plan-side and mesh-side evidence. */
export interface AlignmentSample {
  /** Footprint centroid, plan meters. */
  planX: number;
  planY: number;
  /** Storey elevation, meters (plan z datum); null when unknown. */
  elevation: number | null;
  /** Fragments bounding box of the same space, three.js coords. */
  box: {
    min: { x: number; y: number; z: number };
    max: { x: number; y: number; z: number };
  };
}

/** Three.js translation to apply to the overlay group. */
export interface OverlayOffset {
  x: number;
  y: number;
  z: number;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Median translation from plan coordinates to the Fragments mesh. Horizontal
 * components compare box centers to footprint centroids; the vertical
 * component compares box bottoms to storey elevations (spaces sit ON their
 * storey). Returns null when there are no usable samples (the overlay then
 * renders un-offset — still correct axes, possibly shifted).
 */
export function computeOverlayOffset(
  samples: readonly AlignmentSample[],
): OverlayOffset | null {
  if (samples.length === 0) return null;
  const dx: number[] = [];
  const dz: number[] = [];
  const dy: number[] = [];
  for (const s of samples) {
    const { min, max } = s.box;
    dx.push((min.x + max.x) / 2 - s.planX);
    dz.push((min.z + max.z) / 2 - s.planY);
    if (s.elevation !== null && Number.isFinite(s.elevation)) {
      dy.push(min.y - s.elevation);
    }
  }
  return {
    x: median(dx),
    y: dy.length > 0 ? median(dy) : 0,
    z: median(dz),
  };
}

/** Centroid of a polygon's vertices (cheap, fine for offset estimation). */
export function footprintCentroid(
  footprint: readonly (readonly [number, number])[],
): [number, number] {
  let x = 0;
  let y = 0;
  for (const p of footprint) {
    x += p[0];
    y += p[1];
  }
  return [x / footprint.length, y / footprint.length];
}
